import React, { useEffect, useState } from 'react';
import { Badge, Button, Card, Col, Form, Row, Table } from 'react-bootstrap';
import { format, subDays } from 'date-fns';
import { aiMonitoringAPI } from '../../services';

/**
 * Lệnh sếp 31/10/2026 — THỐNG KÊ NHÂN VIÊN XÁC NHẬN bàn + hoá đơn theo camera. Mỗi tin camera-bill thật có link là
 * một lượt hỏi; nhân viên trong ca nhận tin riêng và xác nhận số bàn + bill trên app Ồn Staff. Tab tổng hợp: số lượt,
 * kết quả (có bill / chưa order / nhận diện sai / không phục vụ), quá hạn chưa ai xác nhận, đang chờ, theo ngày,
 * theo nhân viên và danh sách từng lượt. Quá hạn hiện chỉ thống kê, chưa trừ điểm (công tắc phạt đang tắt).
 */
const OUTCOME = {
  CO_BILL: { bg: 'success', text: 'Có bill' },
  CHUA_ORDER: { bg: 'warning', text: 'Bàn chưa order' },
  NHAN_DIEN_SAI: { bg: 'secondary', text: 'Nhận diện sai' },
  KHONG_PHUC_VU: { bg: 'info', text: 'Khu vực không phục vụ' },
  QUA_HAN: { bg: 'danger', text: 'Quá hạn chưa xác nhận' },
  DANG_CHO: { bg: 'light', text: 'Đang chờ' },
};
const OUTCOME_KEYS = Object.keys(OUTCOME);

const today = () => format(new Date(), 'yyyy-MM-dd');
const weekAgo = () => format(subDays(new Date(), 6), 'yyyy-MM-dd');

const fmt = (v, p = 'HH:mm dd/MM') => {
  if (!v) return '—';
  try {
    return format(new Date(v), p);
  } catch {
    return '—';
  }
};

const regionText = (it) => {
  if (it.detected_table != null) return `Bàn ${it.detected_table}`;
  if (!it.region) return '—';
  const i = it.region.indexOf('~');
  return i >= 0 ? it.region.slice(i + 1) : it.region;
};

const Stat = ({ value, label }) => (
  <div className="cambill-stat">
    <div className="cambill-stat-value">{value ?? '—'}</div>
    <div className="cambill-stat-label">{label}</div>
  </div>
);

const OutcomeBadge = ({ it }) => {
  const o = OUTCOME[it.outcome] || { bg: 'light', text: it.outcome };
  return (
    <>
      <Badge
        bg={o.bg}
        text={o.bg === 'light' || o.bg === 'warning' ? 'dark' : undefined}
      >
        {o.text}
      </Badge>
      {it.late_confirm && (
        <Badge
          bg="danger"
          className="ms-1"
        >
          trễ hạn
        </Badge>
      )}
    </>
  );
};

const AiStaffConfirmTab = ({ branchId, isAdmin }) => {
  const [from, setFrom] = useState(weekAgo);
  const [to, setTo] = useState(today);
  const [allBranches, setAllBranches] = useState(false);
  const [outcome, setOutcome] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tick, setTick] = useState(0);

  // ADMIN đang chọn "Tất cả chi nhánh" ở đầu trang (không có chi nhánh) ⇒ xem gộp mọi chi nhánh.
  const showAll = isAdmin && (allBranches || !branchId);
  const scope = showAll ? 'all' : branchId;

  useEffect(() => {
    if (!scope) return undefined;
    let alive = true;
    aiMonitoringAPI
      .getCambillConfirmStats({
        branch_id: scope,
        from: new Date(`${from}T00:00:00`).toISOString(),
        to: new Date(`${to}T23:59:59.999`).toISOString(),
      })
      .then((d) => {
        if (!alive) return;
        setData(d);
        setError(null);
      })
      .catch((err) => alive && setError(err?.message || 'Không tải được thống kê'))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [scope, from, to, tick]);

  const reload = () => {
    setLoading(true);
    setTick((t) => t + 1);
  };

  if (!scope) return <p className="text-muted">Chọn chi nhánh để xem thống kê.</p>;

  const s = data?.summary;
  const items = (data?.items || []).filter((it) => !outcome || it.outcome === outcome);

  return (
    <div data-testid="thong-ke-xac-nhan">
      <div className="cambill-toolbar">
        <p className="cambill-hint mb-0">
          Mỗi tin camera-bill có link là một lượt hỏi: nhân viên trong ca nhận tin riêng, xác nhận số bàn + hoá đơn trên
          app Ồn Staff. Quá hạn hiện <b>chỉ thống kê, chưa trừ điểm</b>.
        </p>
        <Button
          size="sm"
          variant="outline-primary"
          onClick={reload}
          disabled={loading}
        >
          {loading ? 'Đang tải…' : 'Làm mới'}
        </Button>
      </div>

      <Card className="mb-3">
        <Card.Body className="py-3">
          <Row className="g-3 align-items-end">
            <Col
              xs={6}
              lg={3}
            >
              <Form.Label htmlFor="xn-tu-ngay">Từ ngày</Form.Label>
              <Form.Control
                id="xn-tu-ngay"
                type="date"
                value={from}
                max={to}
                onChange={(e) => {
                  setLoading(true);
                  setFrom(e.target.value || weekAgo());
                }}
              />
            </Col>
            <Col
              xs={6}
              lg={3}
            >
              <Form.Label htmlFor="xn-den-ngay">Đến ngày</Form.Label>
              <Form.Control
                id="xn-den-ngay"
                type="date"
                value={to}
                min={from}
                onChange={(e) => {
                  setLoading(true);
                  setTo(e.target.value || today());
                }}
              />
            </Col>
            <Col
              xs={12}
              sm={6}
              lg={3}
            >
              <Form.Label htmlFor="xn-ket-qua">Kết quả</Form.Label>
              <Form.Select
                id="xn-ket-qua"
                value={outcome}
                onChange={(e) => setOutcome(e.target.value)}
              >
                <option value="">Tất cả</option>
                {OUTCOME_KEYS.map((k) => (
                  <option
                    key={k}
                    value={k}
                  >
                    {OUTCOME[k].text}
                  </option>
                ))}
              </Form.Select>
            </Col>
            {isAdmin && branchId && (
              <Col
                xs={12}
                sm={6}
                lg={3}
              >
                <Form.Check
                  id="xn-moi-chi-nhanh"
                  type="switch"
                  label="Mọi chi nhánh"
                  checked={allBranches}
                  onChange={(e) => {
                    setLoading(true);
                    setAllBranches(e.target.checked);
                  }}
                />
              </Col>
            )}
          </Row>
        </Card.Body>
      </Card>

      {error && <div className="alert alert-danger">{error}</div>}

      {s && (
        <>
          <div
            className="cambill-stats"
            data-testid="xn-tong"
          >
            <Stat
              value={s.total}
              label="Lượt hỏi nhân viên"
            />
            <Stat
              value={`${s.confirmed}${s.confirm_rate != null ? ` (${s.confirm_rate}%)` : ''}`}
              label="Đã xác nhận"
            />
            <Stat
              value={s.overdue}
              label="Quá hạn chưa ai xác nhận"
            />
            <Stat
              value={s.pending}
              label="Đang chờ"
            />
          </div>
          <div
            className="cambill-stats"
            data-testid="xn-ket-qua"
          >
            <Stat
              value={s.outcomes.CO_BILL}
              label="Có bill"
            />
            <Stat
              value={s.outcomes.CHUA_ORDER}
              label="Bàn chưa order"
            />
            <Stat
              value={s.outcomes.NHAN_DIEN_SAI}
              label="Nhận diện sai"
            />
            <Stat
              value={s.outcomes.KHONG_PHUC_VU}
              label="Khu vực không phục vụ"
            />
          </div>
          <div className="cambill-stats">
            <Stat
              value={s.avg_response_minutes != null ? `${s.avg_response_minutes} phút` : '—'}
              label="Thời gian xác nhận TB"
            />
            <Stat
              value={s.confirmed_late}
              label="Xác nhận trễ hạn"
            />
            <Stat
              value={s.postponed}
              label="Lượt báo bàn chưa phục vụ"
            />
            <Stat
              value={s.staff_messages}
              label={`Tin riêng tới nhân viên (${s.no_staff_received} lượt không ai nhận)`}
            />
          </div>
        </>
      )}

      {data?.by_staff?.length > 0 && (
        <Card className="mb-3">
          <Card.Header>Theo nhân viên xác nhận</Card.Header>
          <Table
            responsive
            size="sm"
            className="mb-0"
            data-testid="xn-theo-nv"
          >
            <thead>
              <tr>
                <th>Nhân viên</th>
                <th className="text-end">Lượt</th>
                <th className="text-end">Có bill</th>
                <th className="text-end">Chưa order</th>
                <th className="text-end">Nhận diện sai</th>
                <th className="text-end">Không phục vụ</th>
                <th className="text-end">Trễ hạn</th>
                <th className="text-end">TB phút</th>
              </tr>
            </thead>
            <tbody>
              {data.by_staff.map((r) => (
                <tr key={r.name}>
                  <td>{r.name}</td>
                  <td className="text-end">{r.total}</td>
                  <td className="text-end">{r.CO_BILL}</td>
                  <td className="text-end">{r.CHUA_ORDER}</td>
                  <td className="text-end">{r.NHAN_DIEN_SAI}</td>
                  <td className="text-end">{r.KHONG_PHUC_VU}</td>
                  <td className="text-end">{r.late}</td>
                  <td className="text-end">{r.avg_response_minutes ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card>
      )}

      {data?.by_day?.length > 0 && (
        <Card className="mb-3">
          <Card.Header>Theo ngày</Card.Header>
          <Table
            responsive
            size="sm"
            className="mb-0"
            data-testid="xn-theo-ngay"
          >
            <thead>
              <tr>
                <th>Ngày</th>
                <th className="text-end">Lượt</th>
                {OUTCOME_KEYS.map((k) => (
                  <th
                    key={k}
                    className="text-end"
                  >
                    {OUTCOME[k].text}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.by_day.map((d) => (
                <tr key={d.day}>
                  <td>{fmt(`${d.day}T12:00:00`, 'dd/MM/yyyy')}</td>
                  <td className="text-end">{d.total}</td>
                  {OUTCOME_KEYS.map((k) => (
                    <td
                      key={k}
                      className="text-end"
                    >
                      {d[k]}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </Table>
        </Card>
      )}

      <Card>
        <Card.Header>
          Từng lượt ({items.length}
          {data?.items_truncated ? ', chỉ hiện 300 lượt mới nhất' : ''})
        </Card.Header>
        {items.length === 0 ? (
          <Card.Body className="text-muted">
            {loading ? 'Đang tải…' : 'Chưa có lượt nào trong khoảng ngày này.'}
          </Card.Body>
        ) : (
          <Table
            responsive
            size="sm"
            className="mb-0"
            data-testid="xn-tung-luot"
          >
            <thead>
              <tr>
                <th>Gửi lúc</th>
                {showAll && <th>Chi nhánh</th>}
                <th>Camera thấy</th>
                <th>Kết quả</th>
                <th>Bàn NV chọn</th>
                <th>Bill</th>
                <th className="text-end">Khách / ly</th>
                <th>Người xác nhận</th>
                <th>Nhận tin</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.event_id}>
                  <td className="text-nowrap">{fmt(it.asked_at)}</td>
                  {showAll && <td>{it.branch_name}</td>}
                  <td>{regionText(it)}</td>
                  <td>
                    <OutcomeBadge it={it} />
                    {it.postpone_count > 0 && (
                      <div className="small text-muted">Báo chưa phục vụ {it.postpone_count} lần</div>
                    )}
                  </td>
                  <td>
                    {it.table_numbers.length ? it.table_numbers.join(', ') : '—'}
                    {it.no_order_tables.length > 0 && (
                      <div className="small text-muted">Chưa order: {it.no_order_tables.join(', ')}</div>
                    )}
                  </td>
                  <td>{it.order_numbers.length ? it.order_numbers.join(', ') : '—'}</td>
                  <td className="text-end text-nowrap">
                    {it.guest_count ?? '—'} / {it.drink_total ?? '—'}
                    {it.drinks_short > 0 && <div className="small text-danger">thiếu {it.drinks_short} ly</div>}
                  </td>
                  <td>
                    {it.confirmed_by || '—'}
                    {it.confirmed_at && (
                      <div className="small text-muted">
                        {fmt(it.confirmed_at, 'HH:mm')}
                        {it.response_minutes != null ? ` · ${it.response_minutes} phút` : ''}
                      </div>
                    )}
                  </td>
                  <td className="small">
                    {it.tracked ? (it.staff_names.length ? it.staff_names.join(', ') : 'Không ai nhận') : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>
    </div>
  );
};

export default AiStaffConfirmTab;
